import React, { useState } from 'react';
import styled from 'styled-components';
import { signUpWithFirebase } from '../../firebase/firebase';
import { signup } from '../../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  width: 90%;
  max-width: 450px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
`;

const Logo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  text-align: center;
  margin-bottom: 30px;
  color: #1a2a3a;
  
  span {
    color: #c4a484;
    font-size: 11px;
    display: block;
    letter-spacing: 3px;
    margin-top: 5px;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: #1a2a3a;
  font-weight: 300;
  font-size: 24px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #c4a484;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: #c4a484;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #b89474;
  }
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: 20px;
  color: #666;
  font-size: 14px;
  
  span {
    color: #c4a484;
    cursor: pointer;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 5px;
  text-align: center;
`;

const SuccessMessage = styled.p`
  color: #27ae60;
  font-size: 13px;
  margin-top: 5px;
  text-align: center;
`;

const SignupModal = ({ onClose, onSignup, onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    try {
      // STEP 1: Create user in Firebase Authentication
      const firebaseResult = await signUpWithFirebase(email, password, name);
      
      if (!firebaseResult.success) {
        setError(firebaseResult.error || 'Firebase signup failed');
        setLoading(false);
        return;
      }
      
      console.log('✅ Firebase user created:', firebaseResult.user.uid);
      
      // STEP 2: Store user profile in Neon database
      const dbResult = await signup({
        name: name,
        email: email,
        password: password,
        firebaseUid: firebaseResult.user.uid
      });
      
      if (dbResult.success) {
        setSuccess('Account created! Please check your email to verify your account before logging in.');
        setTimeout(() => {
          onClose();
          onSwitchToLogin();
        }, 4000);
      } else {
        setError(dbResult.error || 'Failed to save user data');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>✕</CloseButton>
        <Logo>
          CEY-VALORA
          <span>ATLANTA • GEORGIA</span>
        </Logo>
        <Title>Create Account</Title>
        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Full Name</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </InputGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          <Button type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Sign Up'}
          </Button>
          <SwitchText>
            Already have an account? <span onClick={onSwitchToLogin}>Login</span>
          </SwitchText>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SignupModal;
